import React from 'react';
import { Box, Avatar, Typography, Badge, Skeleton } from '@mui/material';
import { getInitials } from './chatUtils';
import StyledTooltip from './StyledTooltip';

const Sidebar = ({
    conversations,
    onSelectConversation,
    selectedConversation,
    widgetExpanded,
    isExpanded,
    activeConversationLoading
}) => {
    const styles = {
        sidebar: {
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: "rgba(255, 255, 255, 0.9)",
            boxShadow: '0 1px 6px 1px rgba(0, 0, 0, 0.17)',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
            width: isExpanded ? widgetExpanded ? '220px' : '180px' : '64px',
            overflow: 'hidden',
        },
        header: {
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        hamburgerIcon: {
            color: isExpanded ? 'primary.main' : 'text.secondary',
            borderRadius: '12px',
        },
        avatarList: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 1,
            pb: 1.5,
            px: 1,
            overflowY: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': {
                width: '4px',
            },
            '&::-webkit-scrollbar-track': {
                background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '4px',
            },
            overflow: 'hidden',
        },
        avatarItem: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            py: .7,
            px: .7,
            borderRadius: "12px",
            cursor: 'pointer',
            '&:hover': {
                bgcolor: 'rgba(61, 139, 248, 0.1)',
                transform: 'translateX(2px)',
            },
            transition: 'background-color 0.2s ease, transform 0.2s ease',
            mb: .5,
            justifyContent: isExpanded ? 'flex-start' : 'center',
        },
        avatar: {
            width: 36,
            height: 36,
            background: "#e5e7eb",
            color: "#212121",
            fontSize: "14px",
            fontWeight: "500",
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
        },
        selectedAvatar: {
            bgcolor: 'rgba(61, 139, 248, 0.1)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            transform: 'scale(1.02)',
        },
        avatarName: {
            ml: widgetExpanded ? 1.2 : .8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: widgetExpanded ? '16px' : '14px',
            fontWeight: '500',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'translateX(0)' : 'translateX(-10px)',
        },
        unreadIndicator: {
            ml: 'auto',
            minWidth: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            transition: 'transform 0.2s ease, opacity 0.3s ease',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)' },
            },
        },
        tooltipCard: {
            maxWidth: 220,
            animation: 'fadeIn 0.3s ease',
            '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
            },
        },
        tooltipName: {
            fontWeight: 600,
            mb: 0.5,
        },
        tooltipMessage: {
            fontSize: '0.85rem',
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
        },
        badge: {
            transition: 'transform 0.2s ease',
            '& .MuiBadge-dot': {
                animation: (status) =>
                    status === 'online' ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.2)' },
                    '100%': { transform: 'scale(1)' },
                },
            },
        },
    };

    // Custom tooltip component to show name and last message
    const CustomTooltip = ({ conversation }) => (
        <Box sx={styles.tooltipCard}>
            {activeConversationLoading ? (
                <>
                    <Skeleton sx={{ ...styles.tooltipName, minWidth: 76 }} variant="text" width="100%" height="100%" />
                    <Skeleton sx={{ ...styles.tooltipMessage, minWidth: 76 }} variant="text" width="100%" height="80%" />
                </>
            ) : (
                <>
                    <Typography sx={styles.tooltipName}>{conversation.title}</Typography>
                    {conversation.lastMessage && (
                        <Typography sx={styles.tooltipMessage}>
                            {conversation.lastMessage.content}
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );

    return (
        <Box sx={styles.sidebar} id="sidebar">
            <Box sx={styles.avatarList} id="list">
                {conversations.map((conversation) => (
                    <Box
                        key={conversation.id}
                        sx={{
                            ...styles.avatarItem,
                            ...(selectedConversation?.id === conversation.id && styles.selectedAvatar),
                        }}
                        onClick={() => onSelectConversation(conversation)}
                        id="box"
                    >
                        {isExpanded ? (
                            <>
                                <Avatar sx={styles.avatar}>
                                    {getInitials(conversation.title)}
                                </Avatar>
                                <Typography sx={styles.avatarName} noWrap>
                                    {conversation.title}
                                </Typography>
                                {conversation.unreadCount > 0 && (
                                    <Box sx={styles.unreadIndicator}>
                                        {conversation.unreadCount}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <StyledTooltip
                                title={<CustomTooltip conversation={conversation} />}
                                placement="left"
                            >
                                <Badge
                                    badgeContent={conversation.unreadCount > 0 ? conversation.unreadCount : undefined}
                                    color="primary"
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <Avatar sx={styles.avatar}>
                                        {getInitials(conversation.title)}
                                    </Avatar>
                                </Badge>
                            </StyledTooltip>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default Sidebar; 